'use client';

import { useCallback, useMemo, useReducer } from 'react';
import { flattenDeep, noop } from 'lodash';
import { useSelectedLayoutSegment } from 'next/navigation';
import { createContext, useContextSelector } from 'use-context-selector';
import { useCurrentPageType } from '../../routing';
import type { PageType } from '../../routing/types';
import { layouts } from './layouts';
import type {
  PanelArea,
  PanelLayout,
  PanelLayoutAction,
  PanelLayoutActions,
  PanelLayoutContextProviderProps,
  PanelLayoutContextValue,
  PanelLayoutDefault,
  PanelVisibilityState,
  ShareReviewFinderPageArea,
} from './types';

type PanelLayoutState = {
  visiblePanelsByLayout: PanelVisibilityState;
  /** Up to one area can be marked as "primed" for keyboard shortcuts */
  primedPanelArea: PanelArea | undefined;
  /** LIFO stack with undefined entries removed */
  primeHistoryStack: ReadonlyArray<PanelArea>;
};

const createInitialState = (
  state: PanelLayoutDefault = {}
): PanelLayoutState => ({
  primedPanelArea: undefined,
  primeHistoryStack: [],
  visiblePanelsByLayout: {
    allShares: layouts.allShares.getDefaultVisiblePanels(),
    project: layouts.project.getDefaultVisiblePanels(),
    c2cConnections: layouts.c2cConnections.getDefaultVisiblePanels(),
    c2cConnectionsRSC: layouts.c2cConnectionsRSC.getDefaultVisiblePanels(),
    settings: layouts.settings.getDefaultVisiblePanels(),
    collection: layouts.project.getDefaultVisiblePanels(),
    viewer: layouts.viewer.getDefaultVisiblePanels(),
    workspaces: layouts.workspaces.getDefaultVisiblePanels(),
    shareReviewFinder: [
      ...layouts.shareReviewFinder.getDefaultVisiblePanels(),
      ...(state.visiblePanelsByLayout?.shareReviewFinder ?? []),
    ] as ShareReviewFinderPageArea[],
    shareReviewViewer: layouts.shareReviewViewer.getDefaultVisiblePanels(),
    shareReviewReel: [
      ...layouts.shareReviewReel.getDefaultVisiblePanels(),
      ...(state.visiblePanelsByLayout?.shareReviewReel ?? []),
    ],
    shareBuilder: layouts.shareBuilder.getDefaultVisiblePanels(),
    trash: layouts.trash.getDefaultVisiblePanels(),
    uploadLinks: layouts.uploadLinks.getDefaultVisiblePanels(),
    advancedSearch: layouts.advancedSearch.getDefaultVisiblePanels(),
    uploaderApp: layouts.uploaderApp.getDefaultVisiblePanels(),
    staticProjectNavigation:
      layouts.staticProjectNavigation.getDefaultVisiblePanels(),
  },
});

export const PanelLayoutContext = createContext<PanelLayoutContextValue>({
  ...createInitialState(),
  primePanel: noop,
  togglePanel: noop,
  setVisiblePanels: noop,
});

PanelLayoutContext.displayName = 'PanelLayoutContext';

/**
 * Layouts whose templates can show the AI panel. Its open state is shared
 * across them so the panel survives navigating between the pages they serve
 * (finder, asset viewer, share builder, all shares).
 */
export const AI_CAPABLE_PANEL_LAYOUTS: ReadonlyArray<PanelLayout> = [
  'project',
  'viewer',
  'shareBuilder',
  'allShares',
];

const syncAiPanelAcrossLayouts = (
  visiblePanelsByLayout: PanelVisibilityState,
  isOpen: boolean
): PanelVisibilityState => {
  const next = { ...visiblePanelsByLayout };
  const byLayout = next as unknown as Record<PanelLayout, Array<PanelArea>>;
  AI_CAPABLE_PANEL_LAYOUTS.forEach((layout) => {
    if (!byLayout[layout]) return;
    const visible: Array<PanelArea> = byLayout[layout].filter(
      (area) => area !== 'ai'
    );
    if (isOpen) visible.push('ai');
    byLayout[layout] = visible;
  });
  return next;
};

const reducer = (
  state: PanelLayoutState,
  action: PanelLayoutAction
): PanelLayoutState => {
  if (!action.payload.layout) {
    throw new Error('No panel layout specified.');
  }

  if (!state.visiblePanelsByLayout[action.payload.layout]) {
    throw new Error(`Invalid panel layout: '${action.payload.layout}'`);
  }

  if (action.type === 'TOGGLE' && action.payload.area) {
    const { layout, area } = action.payload;
    const currentVisible = state.visiblePanelsByLayout[layout] as PanelArea[];
    const withToggled = (visible: PanelArea[]): PanelVisibilityState =>
      area === 'ai'
        ? syncAiPanelAcrossLayouts(
            state.visiblePanelsByLayout,
            visible.includes('ai')
          )
        : {
            ...state.visiblePanelsByLayout,
            [layout]: visible,
          };
    if (area === state.primedPanelArea) {
      const nextPrimeHistoryStack = [...state.primeHistoryStack];
      const nextPrimedPanelArea = [...nextPrimeHistoryStack]
        .reverse()
        .find(
          (candidate) =>
            candidate !== area && currentVisible.includes(candidate)
        );
      return {
        ...state,
        primeHistoryStack: nextPrimeHistoryStack,
        primedPanelArea: nextPrimedPanelArea,
        visiblePanelsByLayout: withToggled(
          currentVisible.filter((v) => v !== area)
        ),
      };
    }
    if (area !== state.primedPanelArea && currentVisible.includes(area)) {
      return {
        ...state,
        visiblePanelsByLayout: withToggled(
          currentVisible.filter((v) => v !== area)
        ),
      };
    }
    if (area !== state.primedPanelArea && !currentVisible.includes(area)) {
      return {
        ...state,
        visiblePanelsByLayout: withToggled([...currentVisible, area]),
      };
    }
  }

  if (action.type === 'SET') {
    const { layout, areas } = action.payload;
    const nextVisible = [...areas];
    return {
      ...state,
      primedPanelArea: undefined,
      visiblePanelsByLayout: {
        ...state.visiblePanelsByLayout,
        [layout]: nextVisible,
      },
    };
  }

  if (action.type === 'PRIME') {
    const { layout, area } = action.payload;
    const currentVisible = state.visiblePanelsByLayout[layout] as PanelArea[];
    const nextVisible =
      !area || currentVisible.includes(area)
        ? currentVisible
        : [...currentVisible, area];
    const mostRecentlyPrimed =
      state.primeHistoryStack[state.primeHistoryStack.length - 1];
    const nextPrimeHistoryStack =
      state.primedPanelArea === undefined ||
      state.primedPanelArea === mostRecentlyPrimed
        ? state.primeHistoryStack
        : [...state.primeHistoryStack, state.primedPanelArea];
    return {
      ...state,
      primeHistoryStack: nextPrimeHistoryStack,
      primedPanelArea: area,
      visiblePanelsByLayout:
        area === 'ai' && nextVisible !== currentVisible
          ? syncAiPanelAcrossLayouts(state.visiblePanelsByLayout, true)
          : {
              ...state.visiblePanelsByLayout,
              [layout]: nextVisible,
            },
    };
  }

  return state;
};

export const PanelLayoutContextProvider = ({
  defaultLayout,
  children,
}: PanelLayoutContextProviderProps) => {
  const [{ visiblePanelsByLayout, primedPanelArea }, dispatch] = useReducer(
    reducer,
    createInitialState(defaultLayout)
  );

  /**
   * Open a panel and mark it as the one "primed" for keyboard shortcuts.
   */
  const primePanel = useCallback(
    (layout: PanelLayout, area: PanelArea) => {
      if (primedPanelArea === area) {
        dispatch({ type: 'PRIME', payload: { layout, area: undefined } });
        setTimeout(
          () => dispatch({ type: 'PRIME', payload: { layout, area } }),
          0
        );
      } else {
        dispatch({ type: 'PRIME', payload: { layout, area } });
      }
    },
    [primedPanelArea]
  );

  // Toggle a panel off and on
  const togglePanel = useCallback(
    (layout: PanelLayout | null, area: PanelArea) => {
      if (!layout) return;
      dispatch({
        type: 'TOGGLE',
        payload: {
          layout,
          area,
        },
      });
    },
    []
  );

  // To set all panels' visibility at once, e.g. from a route change
  const setVisiblePanels = useCallback(
    (layout: PanelLayout, areas: Array<PanelArea>) => {
      dispatch({
        type: 'SET',
        payload: {
          layout,
          areas,
        },
      });
    },
    []
  );

  const comparableVisiblePanelsByLayout = flattenDeep<PanelArea>(
    Object.values(visiblePanelsByLayout)
  ).join(',');

  const value = useMemo(
    () => ({
      primedPanelArea,
      primePanel,
      setVisiblePanels,
      togglePanel,
      visiblePanelsByLayout,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      comparableVisiblePanelsByLayout,
      primedPanelArea,
      primePanel,
      setVisiblePanels,
      togglePanel,
    ]
  );

  return (
    <PanelLayoutContext.Provider value={value}>
      {children}
    </PanelLayoutContext.Provider>
  );
};

export const usePanelLayoutActions = (): PanelLayoutActions => {
  const primedPanelArea = useContextSelector(
    PanelLayoutContext,
    (value) => value?.primedPanelArea
  );
  const togglePanel = useContextSelector(
    PanelLayoutContext,
    (value) => value?.togglePanel
  );
  const primePanel = useContextSelector(
    PanelLayoutContext,
    (value) => value?.primePanel
  );
  const setVisiblePanels = useContextSelector(
    PanelLayoutContext,
    (value) => value?.setVisiblePanels
  );
  return {
    primedPanelArea,
    primePanel,
    setVisiblePanels,
    togglePanel,
  };
};

export const useVisiblePanel = (
  layout: PanelLayout | null,
  panelName: PanelArea
) => {
  return useContextSelector(PanelLayoutContext, (value) =>
    // @ts-expect-error
    Boolean(value?.visiblePanelsByLayout?.[layout]?.includes(panelName))
  );
};

export const getPanelLayoutByPageType = (
  pageType: PageType,
  selectedLayoutSegment?: string | null
): PanelLayout | null => {
  if (pageType === 'folderViewer') return 'viewer';
  if (pageType === 'folder') return 'project';
  if (pageType === 'collectionViewer') return 'viewer';
  if (pageType === 'collection') return 'project';
  if (pageType === 'shareReviewFinder' && selectedLayoutSegment === 'view')
    return 'shareReviewViewer';
  if (pageType === 'shareReviewFinder') return 'shareReviewFinder';
  if (pageType === 'shareBuilderViewer') return 'viewer';
  if (pageType === 'shareBuilder') return 'shareBuilder';
  if (pageType === 'shareReviewReel') return 'shareReviewReel';
  if (pageType === 'allShares') return 'allShares';
  if (pageType === 'uploadLinks') return 'uploadLinks';
  if (pageType === 'c2cConnections') return 'c2cConnectionsRSC';
  if (pageType === 'workspaces') return 'workspaces';
  if (pageType === 'trash') return 'trash';
  if (pageType === 'search') return 'advancedSearch';
  return null;
};

/** The panel layout for the page currently being viewed. */
export const useCurrentPanelLayout = (): PanelLayout | null => {
  const pageType = useCurrentPageType();
  const selectedLayoutSegment = useSelectedLayoutSegment();
  return getPanelLayoutByPageType(pageType, selectedLayoutSegment);
};

export const useIsPanelOpen = (panelName: PanelArea): boolean => {
  const panelLayout = useCurrentPanelLayout();
  return useVisiblePanel(panelLayout, panelName);
};
